import React, { useState, useRef, lazy, Suspense } from 'react'
import { useOptimizedScroll } from '../hooks/useOptimizedScroll'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { motion, useScroll, useTransform } from 'framer-motion'
import { ServiceType } from '../types'
import { useServiceMonitoring } from '../hooks/useServiceMonitoring'
import { useDivisionRouting } from '../hooks/useDivisionRouting'
import { StatusNotification } from './StatusNotification'

// import LanguageSwitcher from './LanguageSwitcher' // Commented out - browsers have built-in translation
import { getServiceConfig, getServiceUrl, isDevelopmentMode } from '../config/services'

// Import performance optimizations
import '../styles/performance.css'

// Lazy load DivisionShowcase for code splitting
const DivisionShowcase = lazy(() =>
  import('./sections/DivisionShowcase').then(module => ({
    default: module.DivisionShowcase
  }))
)

// Import AboutSection
import { AboutSection } from './sections/AboutSection'
import { MobileFooter } from './MobileFooter'
import { useIsMobile } from '../hooks/useMediaQuery'

// Hero Section Component with Premium Animations
const HeroSection: React.FC = () => {
  const _heroRef = useRef<HTMLElement>(null)
  const { scrollY } = useScroll()

  // Parallax transforms for background elements (0.3x scroll speed)
  const gradientY = useTransform(scrollY, [0, 500], [0, 150])

  // Detect prefers-reduced-motion
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  )

  // Character-by-character wave animation for headline
  const headlineText = "Transform Your Business Operations"
  const headlineVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.03,
        delayChildren: 0.2
      }
    }
  }

  // Subtitle animation
  const subtitleVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        delay: 1.5,
        ease: [0.4, 0.0, 0.2, 1]
      }
    }
  }

  const charVariants = {
    hidden: { opacity: 0, x: -20, y: 10 },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.4, 0.0, 0.2, 1]
      }
    }
  }

  // Subtle floating animation for headline (after reveal)
  const floatingVariants = {
    animate: {
      y: [0, -8, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut",
        delay: 2 // Start after character reveal completes
      }
    }
  }

  return (
    <section
      ref={_heroRef}
      className="relative z-40 min-h-screen flex items-center overflow-hidden"
      style={{ backgroundColor: '#F7F5F5' }}
    >
      {/* Animated Background Gradient Orbs */}
      <motion.div
        className="absolute -top-40 -left-40 w-96 h-96 rounded-full opacity-20 blur-3xl"
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          willChange: prefersReducedMotion.current ? 'auto' : 'transform'
        }}
        animate={
          prefersReducedMotion.current
            ? {}
            : {
              scale: [1, 1.2, 1],
              x: [0, 50, 0],
              y: [0, 30, 0],
            }
        }
        transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute top-1/2 -right-40 w-96 h-96 rounded-full opacity-20 blur-3xl"
        style={{
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          willChange: prefersReducedMotion.current ? 'auto' : 'transform'
        }}
        animate={
          prefersReducedMotion.current
            ? {}
            : {
              scale: [1, 1.3, 1],
              x: [0, -50, 0],
              y: [0, -30, 0],
            }
        }
        transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute bottom-20 left-1/3 w-80 h-80 rounded-full opacity-15 blur-3xl"
        style={{
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          willChange: prefersReducedMotion.current ? 'auto' : 'transform'
        }}
        animate={
          prefersReducedMotion.current
            ? {}
            : {
              scale: [1, 1.15, 1],
              x: [0, 30, 0],
              y: [0, -20, 0],
            }
        }
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Parallax Background Gradient */}
      <motion.div
        className="absolute inset-0 opacity-30"
        style={{
          y: prefersReducedMotion.current ? 0 : gradientY,
          background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.1) 0%, rgba(155, 89, 182, 0.1) 50%, rgba(46, 204, 113, 0.1) 100%)',
          willChange: prefersReducedMotion.current ? 'auto' : 'transform'
        }}
      />



      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Character-by-Character Headline Reveal with Floating Animation */}
        <motion.div
          animate={prefersReducedMotion.current ? {} : "animate"}
          variants={floatingVariants}
        >
          <motion.h1
            className="mb-8 leading-tight tracking-tight"
            style={{
              color: '#1A1A1A',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 600,
              letterSpacing: '-0.02em',
              fontSize: 'clamp(2rem, 8vw, 4.5rem)',
              lineHeight: 'clamp(1.1, 1.2, 1.3)',
            }}
            initial={prefersReducedMotion.current ? "visible" : "hidden"}
            animate="visible"
            variants={headlineVariants}
          >
            {headlineText.split(' ').map((word, wordIndex) => (
              <span key={`word-${wordIndex}`} style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
                {word.split('').map((char, charIndex) => (
                  <motion.span
                    key={`${wordIndex}-${charIndex}`}
                    variants={prefersReducedMotion.current ? {} : charVariants}
                    style={{ display: 'inline-block' }}
                  >
                    {char}
                  </motion.span>
                ))}
                {wordIndex < headlineText.split(' ').length - 1 && (
                  <span style={{ display: 'inline-block', width: '0.3em' }}>&nbsp;</span>
                )}
              </span>
            ))}
          </motion.h1>
        </motion.div>

        {/* Subtitle with service description - Responsive sizing */}
        <motion.p
          className="mb-16 max-w-3xl mx-auto leading-relaxed"
          style={{
            color: '#666666',
            fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
            fontWeight: 500,
            fontSize: 'clamp(1rem, 4vw, 1.5rem)',
            lineHeight: 'clamp(1.5, 1.6, 1.7)',
          }}
          initial={prefersReducedMotion.current ? "visible" : "hidden"}
          animate="visible"
          variants={subtitleVariants}
        >
          Recruitment to trade intelligence.{' '}
          <span style={{ color: '#1A1A1A', fontWeight: 600 }}>One platform, unlimited possibilities.</span>
        </motion.p>

      </div>
    </section>
  )
}

export const UnifiedLandingPage: React.FC = () => {
  const { t, i18n } = useTranslation()
  const [activeDivision, setActiveDivision] = useState<'software' | 'hardware' | 'services'>('services')
  const [activeProduct, setActiveProduct] = useState<string | null>(null)
  const isMobile = useIsMobile()

  // Add loading state for i18n
  const [i18nReady, setI18nReady] = useState(i18n.isInitialized)

  // Division routing hook
  const { navigateToDivision, updateUrl } = useDivisionRouting({
    onDivisionChange: (division) => {
      setActiveDivision(division)
      setActiveProduct(null)
    },
    onProductRoute: (product) => {
      setActiveProduct(product)
      // Determine which division this product belongs to
      const productToDivision: Record<string, 'software' | 'hardware' | 'services'> = {
        codered: 'software',
        beyondreal: 'software',
        macx: 'software',
        'macx-handheld': 'hardware',
        'melanin-devices': 'hardware',
      }
      const division = productToDivision[product]
      if (division) {
        setActiveDivision(division)
      }
    },
  })

  // Debug: Log translation function
  React.useEffect(() => {
    console.log('i18n ready:', i18nReady)
    console.log('Current language:', i18n.language)
    console.log('Test translation:', t('nav.services'))
  }, [i18nReady, i18n.language, t])

  // Use the service monitoring hook
  const {
    services,
    isLoading,
    apiConnected,
    statusNotifications,
    clearNotifications,
    dismissNotification,
  } = useServiceMonitoring(30000, true) // Poll every 30 seconds

  // Scroll to top on mount only if we're on the home page (no division/product route)
  React.useEffect(() => {
    const path = window.location.pathname.substring(1) // Remove leading slash - use window.location directly
    const isDivisionOrProductRoute = ['software', 'hardware', 'services', 'codered', 'beyondreal', 'macx', 'macx-handheld', 'melanin-devices'].includes(path)

    if (!isDivisionOrProductRoute) {
      // Immediate scroll to top for home page
      window.scrollTo(0, 0)
      // Also remove any hash from URL
      if (window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname)
      }
    }
  }, [])

  React.useEffect(() => {
    // Handle i18n initialization
    if (i18n.isInitialized) {
      setI18nReady(true)
    } else {
      const handleInitialized = () => {
        setI18nReady(true)
      }

      i18n.on('initialized', handleInitialized)

      return () => {
        i18n.off('initialized', handleInitialized)
      }
    }
  }, [i18n])

  // Optimized scroll detection for floating navbar and footer scroll-over effects
  const { isScrolled: _isScrolled, scrollProgress } = useOptimizedScroll()

  const handleServiceClick = (serviceType: ServiceType) => {
    const service = services.find(s => s.id === serviceType)
    const serviceConfig = getServiceConfig(serviceType)

    if (
      service &&
      serviceConfig &&
      service.status === 'active' &&
      service.isActive &&
      !service.isComingSoon
    ) {
      // Use environment-aware URL generation
      const serviceUrl = getServiceUrl(serviceConfig)

      // In development, open in new tab for better development experience
      if (isDevelopmentMode()) {
        window.open(serviceUrl, '_blank')
      } else {
        window.location.href = serviceUrl
      }
    }
  }

  // Show loading state while i18n is initializing
  if (!i18nReady) {
    return (
      <div className="min-h-screen bg-black text-white font-sans flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Status Notifications */}
      <StatusNotification
        notifications={statusNotifications}
        onDismiss={dismissNotification}
        onClearAll={clearNotifications}
      />

      {/* Main Content Wrapper with Rounded Bottom Corners - Covers footer on scroll */}
      <div
        className="min-h-screen font-sans relative"
        style={{
          backgroundColor: '#F7F5F5',
          color: '#1A1A1A',
          borderBottomLeftRadius: scrollProgress > 0 ? `${Math.min(scrollProgress * 48, 48)}px` : '48px',
          borderBottomRightRadius: scrollProgress > 0 ? `${Math.min(scrollProgress * 48, 48)}px` : '48px',
          boxShadow: scrollProgress > 0
            ? `0 ${20 + scrollProgress * 20}px ${60 + scrollProgress * 40}px rgba(0, 0, 0, ${0.2 + scrollProgress * 0.15})`
            : '0 20px 60px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
          overflowX: 'hidden',
          zIndex: 10,
          paddingBottom: '60px', // Stop exactly at footer header line
          transition: 'border-radius 0.1s ease-out, box-shadow 0.2s ease-out',
          maxWidth: '100vw',
          width: '100%',
        }}
      >
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.02)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
        </div>

        {/* Navbar is now global - no need for individual page navbars */}

        {/* Hero Section - Premium Redesign */}
        <HeroSection />

        {/* Unified Division Showcase with Services */}
        <Suspense fallback={
          <div className="relative z-30 py-24 flex items-center justify-center" style={{ backgroundColor: '#F7F5F5', minHeight: '400px' }}>
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-gray-300 border-t-gray-600 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Loading services...</p>
            </div>
          </div>
        }>
          <DivisionShowcase
            services={services}
            onServiceClick={handleServiceClick}
            isLoading={isLoading}
            apiConnected={apiConnected}
            activeDivision={activeDivision}
            onDivisionChange={(division) => {
              setActiveDivision(division)
              updateUrl(division, true)
            }}
            activeProduct={activeProduct}
          />
        </Suspense>

        {/* About Section - Enhanced with Glassmorphism and Animations */}
        <AboutSection />

      </div>

      {/* Conditional Footer - Mobile Accordion or Desktop Grid */}
      {isMobile ? (
        <MobileFooter />
      ) : (
        <footer
          id="contact"
          className="relative"
          style={{
            backgroundColor: '#0E0E0E',
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            paddingTop: '140px', // Extended padding to create background behind rounded corners
            paddingBottom: '40px',
            marginTop: '-80px' // Pull footer up to extend background behind page content
          }}
        >
          {/* Floating Gradient Divider */}
          <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Hero Footer Section */}
            <div className="text-center mb-16">
              <h2
                className="text-3xl md:text-4xl mb-4"
                style={{
                  color: '#FFFFFF',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600,
                  letterSpacing: '-0.02em'
                }}
              >
                Build. Innovate. Simplify.
              </h2>
              <p
                className="text-base md:text-lg mb-8 max-w-2xl mx-auto"
                style={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 400
                }}
              >
                Discover how Mernaki is reshaping software, hardware, and global trade systems.
              </p>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <span
                  className="px-6 py-2.5 rounded-full text-sm"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    color: 'rgba(255, 255, 255, 0.4)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 500,
                    backdropFilter: 'blur(10px)',
                    filter: 'blur(1px)',
                    userSelect: 'none',
                    pointerEvents: 'none',
                    cursor: 'not-allowed'
                  }}
                >
                  Explore Products
                </span>
                <a
                  href="#services"
                  className="px-6 py-2.5 rounded-full text-sm transition-all duration-300"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    color: 'rgba(255, 255, 255, 0.9)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 500,
                    backdropFilter: 'blur(10px)',
                    textDecoration: 'none',
                    display: 'inline-block'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.12)'
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)'
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)'
                  }}
                >
                  View Services
                </a>
              </div>
            </div>

            {/* Main Footer Columns */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
              {/* Column 1 - Brand */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src="/Mernaki_Logo_Pack/transparent/mernaki-128.png"
                    alt="Mernaki Logo"
                    className="object-contain"
                    style={{ height: '42px', width: 'auto', opacity: 0.85, filter: 'brightness(0) invert(1)' }}
                    loading="lazy"
                  />
                  <h3
                    className="text-lg"
                    style={{
                      color: '#FFFFFF',
                      fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                      fontWeight: 400
                    }}
                  >
                    Mernaki
                  </h3>
                </div>
                <p
                  className="text-sm leading-relaxed"
                  style={{
                    color: 'rgba(255, 255, 255, 0.5)',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 400
                  }}
                >
                  Building intelligent systems that power global innovation.
                </p>
              </div>

              {/* Column 2 - Explore */}
              <div>
                <h3
                  className="text-sm mb-4"
                  style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em'
                  }}
                >
                  Explore
                </h3>
                <ul className="space-y-3">
                  <li>
                    <span
                      className="text-sm"
                      style={{
                        color: 'rgba(255, 255, 255, 0.3)',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontWeight: 400,
                        filter: 'blur(2px)',
                        userSelect: 'none',
                        pointerEvents: 'none'
                      }}
                    >
                      Software
                    </span>
                  </li>
                  <li>
                    <span
                      className="text-sm"
                      style={{
                        color: 'rgba(255, 255, 255, 0.3)',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontWeight: 400,
                        filter: 'blur(2px)',
                        userSelect: 'none',
                        pointerEvents: 'none'
                      }}
                    >
                      Hardware
                    </span>
                  </li>
                  <li>
                    <button
                      onClick={() => navigateToDivision('services')}
                      className="text-sm transition-all duration-300"
                      style={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontWeight: 400,
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'
                      }}
                    >
                      Services
                    </button>
                  </li>
                  <li>
                    <Link
                      to="/about"
                      className="text-sm transition-all duration-300"
                      style={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontWeight: 400,
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'
                      }}
                    >
                      About
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/partners"
                      className="text-sm transition-all duration-300"
                      style={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontWeight: 400,
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'
                      }}
                    >
                      Partners
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Column 3 - Company */}
              <div>
                <h3
                  className="text-sm mb-4"
                  style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em'
                  }}
                >
                  Company
                </h3>
                <ul className="space-y-3">
                  <li>
                    <Link
                      to="/team"
                      className="text-sm transition-all duration-300"
                      style={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontWeight: 400,
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'
                      }}
                    >
                      Team
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/careers"
                      className="text-sm transition-all duration-300"
                      style={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontWeight: 400,
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'
                      }}
                    >
                      Careers
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/partnerships"
                      className="text-sm transition-all duration-300"
                      style={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontWeight: 400,
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'
                      }}
                    >
                      Partnerships
                    </Link>
                  </li>
                  <li>
                    <span
                      className="text-sm"
                      style={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontWeight: 400
                      }}
                    >
                      Press
                    </span>
                  </li>
                </ul>
              </div>

              {/* Column 4 - Support */}
              <div>
                <h3
                  className="text-sm mb-4"
                  style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em'
                  }}
                >
                  Support
                </h3>
                <ul className="space-y-3">
                  <li>
                    <Link
                      to="/help"
                      className="text-sm transition-all duration-300"
                      style={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontWeight: 400,
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'
                      }}
                    >
                      Help Center
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/contact"
                      className="text-sm transition-all duration-300"
                      style={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontWeight: 400,
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'
                      }}
                    >
                      Contact
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/privacy-policy"
                      className="text-sm transition-all duration-300"
                      style={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontWeight: 400,
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'
                      }}
                    >
                      Privacy Policy
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/impressum"
                      className="text-sm transition-all duration-300"
                      style={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontWeight: 400,
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'
                      }}
                    >
                      Impressum
                    </Link>
                  </li>
                </ul>
              </div>

            </div>

            {/* Showcase Row - Flagship Projects */}
            <div className="mb-10 pb-10 border-b border-white/10">
              <div className="flex items-center justify-center gap-3 flex-wrap text-sm">
                <span
                  className="text-sm"
                  style={{
                    color: 'rgba(255, 255, 255, 0.3)',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 400,
                    filter: 'blur(2px)',
                    userSelect: 'none',
                    pointerEvents: 'none'
                  }}
                >
                  Project Crimson
                </span>
                <span style={{ color: 'rgba(255, 255, 255, 0.2)', userSelect: 'none' }}>•</span>
                <span
                  className="text-sm"
                  style={{
                    color: 'rgba(255, 255, 255, 0.3)',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 400,
                    filter: 'blur(2px)',
                    userSelect: 'none',
                    pointerEvents: 'none'
                  }}
                >
                  Project Velocity
                </span>
                <span style={{ color: 'rgba(255, 255, 255, 0.2)', userSelect: 'none' }}>•</span>
                <span
                  className="text-sm"
                  style={{
                    color: 'rgba(255, 255, 255, 0.3)',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 400,
                    filter: 'blur(2px)',
                    userSelect: 'none',
                    pointerEvents: 'none'
                  }}
                >
                  Project Nexus
                </span>
              </div>
            </div>

            {/* Contact Line + Legal */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-sm">
                <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Munich, Germany</span>
                <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>—</span>
                <a
                  href="mailto:info@mernaki.de"
                  className="transition-all duration-300"
                  style={{
                    color: 'rgba(255, 255, 255, 0.4)',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.4)'}
                >
                  Info@mernaki.de
                </a>
              </div>
              <p
                className="text-sm"
                style={{
                  color: 'rgba(255, 255, 255, 0.3)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 300
                }}
              >
                © {new Date().getFullYear()} Mernaki. Crafted in Germany.
              </p>
            </div>
          </div>
        </footer>
      )}

    </>
  )
}
